import { Box, Flex, HStack, usePrefersReducedMotion } from '@chakra-ui/react';
import { ReactNode, useCallback, useEffect, useState } from 'react';

export interface NotificationBanner {
  id: string;
  content: ReactNode;
}

interface NotificationBannerCarouselProps {
  banners: NotificationBanner[];
  label?: string;
  autoPlayIntervalMs?: number;
}

export const DEFAULT_AUTOPLAY_INTERVAL_MS = 6000;

// `inert` keeps off-screen slides out of the tab order and the accessibility
// tree. It is a boolean attribute, so it is only set when it should apply.
const inertProps = (isInactive: boolean) =>
  (isInactive ? { inert: '' } : {}) as { inert?: string };

export default function NotificationBannerCarousel({
  banners,
  label = 'Notifications',
  autoPlayIntervalMs = DEFAULT_AUTOPLAY_INTERVAL_MS,
}: NotificationBannerCarouselProps) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const bannerCount = banners.length;
  const hasControls = bannerCount > 1;

  const pause = useCallback(() => setIsPaused(true), []);
  const resume = useCallback(() => setIsPaused(false), []);

  // Keep the active slide in range when a banner leaves the rotation.
  useEffect(() => {
    setActiveIndex((index) => (index < bannerCount ? index : 0));
  }, [bannerCount]);

  useEffect(() => {
    if (!hasControls || isPaused) return;

    const timer = window.setInterval(() => {
      setActiveIndex((index) => (index + 1) % bannerCount);
    }, autoPlayIntervalMs);

    return () => window.clearInterval(timer);
  }, [hasControls, isPaused, bannerCount, autoPlayIntervalMs]);

  if (bannerCount === 0) return null;

  return (
    <Box
      data-testid="notification-banner-carousel"
      role="region"
      aria-roledescription="carousel"
      aria-label={label}
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
    >
      <Box overflow="hidden" borderRadius="16px">
        <Flex
          data-testid="notification-banner-track"
          align="stretch"
          transform={`translateX(-${activeIndex * 100}%)`}
          transition={
            prefersReducedMotion ? undefined : 'transform 400ms ease-in-out'
          }
          paddingTop=".625rem"
        >
          {banners.map((banner, index) => (
            <Box
              key={banner.id}
              flex="0 0 100%"
              maxW="100%"
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${bannerCount}`}
              {...inertProps(index !== activeIndex)}
            >
              {banner.content}
            </Box>
          ))}
        </Flex>
      </Box>

      {hasControls && (
        <HStack spacing={2} justify="center" mt={3}>
          {banners.map((banner, index) => (
            <Box
              key={banner.id}
              as="button"
              type="button"
              aria-label={`Show notification ${index + 1} of ${bannerCount}`}
              aria-current={index === activeIndex}
              onClick={() => setActiveIndex(index)}
              h="8px"
              w={index === activeIndex ? '24px' : '8px'}
              borderRadius="full"
              bg={index === activeIndex ? 'richBlack.500' : 'ash.300'}
              transition={
                prefersReducedMotion
                  ? undefined
                  : 'width 200ms ease, background-color 200ms ease'
              }
            />
          ))}
        </HStack>
      )}
    </Box>
  );
}
